import React, { PropsWithChildren, useRef, useState } from "react";
import { postFileUploadURL, uploadFile, upsertFile } from "../http/chapterApi";
import SVGError from "../icons/Error.svg?react";
import FileUpload from "../icons/FileUpload.svg?react";
import { UpsertDocumentModalType } from "../types/apiTypes";
import { DocumentFile, PostDocumentFileBody } from "../types/types";
import styles from "./DocumentUpload.module.css";
import useAbortController from "./hooks/useAbortController";
import Button from "./ui/Button";
import Icon from "./ui/Icon";
import Modal from "./ui/Modal";

const allowedTypes = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
];

const maxFileSize = 10 * 1024 * 1024;

interface Props {
  chapterId: number;
  files: DocumentFile[];
  onFileUploaded: (file: DocumentFile) => void;
}

const DocumentUpload = ({ chapterId, files, onFileUploaded }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [invalidType, setInvalidType] = useState(false);
  const [invalidSize, setInvalidSize] = useState(false);
  const [modal, setModal] = useState<UpsertDocumentModalType | undefined>();
  const [_, resetAbortController] = useAbortController();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileToUpload = e.target.files?.[0];

    if (!fileToUpload) return;

    const fileToOverwrite = files.find(
      (existingFile) => existingFile.name === fileToUpload.name,
    );

    const invalidSize =
      fileToUpload.size < 1 || fileToUpload.size >= maxFileSize;
    const invalidType = !allowedTypes.includes(fileToUpload.type);

    setInvalidSize(invalidSize);
    setInvalidType(invalidType);

    if (invalidSize || invalidType) {
      return;
    }

    const body: PostDocumentFileBody = {
      name: fileToUpload.name,
    };

    fileToOverwrite
      ? setModal({ file: fileToUpload, body })
      : handleUpload(fileToUpload, body);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setInvalidSize(false);
    setInvalidType(false);
  };

  const handleUpload = async (
    fileToUpload: File,
    file: PostDocumentFileBody,
  ) => {
    const abortController = resetAbortController();

    try {
      const policyResult = await postFileUploadURL(chapterId, file.name);
      if (!policyResult.url) {
        throw new Error("Ungültige Upload Policy");
      }

      const uploadResult = await uploadFile(
        policyResult,
        abortController.signal,
        fileToUpload,
      );

      if (uploadResult.data) {
        const response = await upsertFile(chapterId, file);
        onFileUploaded(response);
      }
    } catch (error) {
      console.error("Fehler beim Hochladen:", error);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <div className={styles.container}>
        <input
          className={styles.input}
          ref={fileInputRef}
          id="file"
          type="file"
          onChange={handleFileChange}
          accept=".pdf, .docx, .doc, .xlsx, .xls., .odt, .ods"
        />
        <Button
          buttonProps={{
            onClick: triggerFileInput,
            className: styles["btn-upload"],
          }}
        >
          <Icon className={styles["btn-icon"]} glyph={FileUpload} />
          Dokument hochladen
        </Button>
        {invalidType && (
          <ErrorMessage>
            Der ausgewählte Dateityp wird nicht unterstützt. Erlaubte
            Dateiformate sind: .pdf, .docx, .doc, .xlsx, .xls., .odt, .ods
          </ErrorMessage>
        )}

        {invalidSize && (
          <ErrorMessage>
            Die ausgewählte Datei muss größer als 0 KB sein und kleiner als{" "}
            {maxFileSize / (1024 * 1024)} MB.
          </ErrorMessage>
        )}
      </div>
      {modal && (
        <OverwriteDocumentModal
          fileName={modal.body.name}
          onClose={() => setModal(undefined)}
          onConfirm={() => {
            handleUpload(modal.file, modal.body);
          }}
        />
      )}
    </>
  );
};

const ErrorMessage = ({ children }: PropsWithChildren) => (
  <div className={styles["error-container"]}>
    <Icon glyph={SVGError} />
    <p>{children}</p>
  </div>
);

interface OverwriteDocumentModalProps {
  fileName: string;
  onClose: () => void;
  onConfirm: () => void;
}

const OverwriteDocumentModal = ({
  fileName,
  onClose,
  onConfirm,
}: OverwriteDocumentModalProps) => (
  <Modal onClose={onClose}>
    <div>
      <p className={styles.text}>
        Die Datei <strong>{fileName}</strong> existiert bereits.
        <br />
        Beim Speichern wird sie überschrieben und unwiderruflich gelöscht.
      </p>
      <div className={styles.buttons}>
        <Button
          buttonProps={{
            onClick: onClose,
          }}
        >
          Abbrechen
        </Button>
        <Button
          buttonProps={{
            onClick: () => {
              onConfirm();
              onClose();
            },
          }}
        >
          Überschreiben
        </Button>
      </div>
    </div>
  </Modal>
);

export default DocumentUpload;
